'use client'
import { useMemo, useEffect, type FC } from 'react'
import styled from 'styled-components'
import { Link } from '../base/Link'
import { useTranslator, useEntryAnimation } from '@/hooks'
import { getLinks } from './Navbar'
import { createUnderline, defaultGrid, designGrid, font10, font12_Nav, font20, innerGrid, mq, vw, font18 } from '@/styles'

const getRrssLinks = (links) => links.map((item, idx) => {
  return (<dd key={idx}>
    <a href={item.href} title={item.name}>
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img src={`/images/svg/i--rrss--${item.name.toLowerCase()}.svg`} alt={item.name} />
    </a>
  </dd>)
})

let firstTime = true
export const Footer:FC = () => {
  const { t } = useTranslator()
  const Items = useMemo(() => getLinks(t('project.nav.links')), [t])
  const RrssLinks = useMemo(() => getRrssLinks(t('project.footer.rrss')), [t])
  const { setShouldFadeIn } = useEntryAnimation()

  useEffect(() => {
    if(!firstTime) return
    setTimeout(() => {
      setShouldFadeIn(false)
      firstTime = false
    }, 1500)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <FooterStyled>
      <hr />
      <address id='address'>
        <span>{t('project.footer.address')} & {t('project.footer.contact')}</span>
        <p >Portella <br />Calle de la Portella nº9<br />07001 Palma de Mallorca</p>
        <a href='https://maps.app.goo.gl/AF22ioAYbH7q1E1t8' target='_blank'>{t('project.footer.view_map')}</a>
				<br />
        <a href='tel:0034871626263'>+34 871 62 62 63</a>
        <a href='mailto:welcome@portellapalma.com'>welcome@portellapalma.com</a><br />
      </address>
      <div className='availability'>
        <div className='codes'>
          <dl>
            <dt>GDS Codes</dt>
            <dd>Amadeus: <span>YX PMIPOR</span></dd>
            <dd>Sabre: <span>YX 606448</span></dd>
            <dd>Galileo Apollo : <span>YX I1538</span></dd>
            <dd>Worldspan: <span>YX PMIPO</span></dd>
            <dd>DHISCO: <span>GL 44098</span></dd>
          </dl>
        </div>
      </div>
      <div className='links'>
        <ul>
          {Items}
        </ul>
        <dl>
          {RrssLinks}
        </dl>
      </div>
      <p>
        <span>© PORTELLA {new Date().getFullYear()}</span>
				<span className='links--legal'>
					{(t('project.footer.legal_links') as unknown as{ name: string, href: string, internal: boolean }[]).map((item) => (
						<Link key={item.name} href={item.href}>{item.name}</Link>
					))}
				</span>
      </p>
    </FooterStyled>
  )
}


const FooterStyled = styled.footer`
  ${designGrid({})}
  margin-top: auto;
  padding: 0 0 ${vw(60, 'mobile')};

  ${mq.greaterThan('tablet')} {
    padding: 0 0 ${vw(20, 'desktop')};
  }

  hr {
    background-color: var(--color-black);
    display: flex;
    flex-direction: row;
    grid-area: 1 / span 6;
    height: 1px;
    margin-bottom: ${vw(50, 'mobile')};
    opacity: .2;
    width: 100%;
    margin-top: 0;

    ${mq.greaterThan('tablet')} {
      margin-bottom: ${vw(50, 'desktop')};
      grid-column: 1 / span 12;
    }

  }

  address {
		align-items: flex-start;
		display: flex;
		flex-direction: column;
    grid-column: 1 / span 6;

    ${mq.greaterThan('tablet')} {
      grid-column: 1 / span 4;
      grid-row: 2;
    }

		p,
    a {
			display: inline-block;
      ${font12_Nav(true, 400)}
      letter-spacing: -.2px;
			text-align: left;

      ${mq.greaterThan('tablet')} {
        ${font12_Nav(false, 400)}
        letter-spacing: unset;
      }
    }

    a {
      ${createUnderline({ reverse: true })}
			margin-bottom: 4px;
    }

    p {
      margin-bottom: ${vw(5, 'mobile')};

      ${mq.greaterThan('tablet')} {
        margin-bottom: ${vw(5, 'desktop')};
      }

      ${mq.greaterThan('desktop')} {
        margin-bottom: 5px;
      }
    }

    span {
      ${font12_Nav(true, 400)}
      display: block;
      margin-bottom: ${vw(10, 'mobile')};
			opacity: .55;

      /* ${mq.greaterThan('nexus7')} {
        margin-bottom: ${vw(10, 'tablet')};
      } */

      ${mq.greaterThan('tablet')} {
        ${font12_Nav(false, 400)}
        margin-bottom: ${vw(10, 'desktop')};
      }

      ${mq.greaterThan('desktop')} {
        margin-bottom: 10px;
      }
    }
  }

  .availability {
    grid-column: 1 / span 6;
    margin-top: ${vw(50, 'mobile')};

    /* ${mq.greaterThan('nexus7')} {
      margin-top: ${vw(50, 'tablet')};
    } */

    ${mq.greaterThan('tablet')} {
      grid-column: 5 / span 4;
      grid-row: 2;
      margin-top: 0;
    }

    a,p {
      ${font20(true)}
      letter-spacing: -.2px;
      text-align: justify;

      ${mq.greaterThan('tablet')} {
        ${font18(false)}
        letter-spacing: unset;
      }
    }

    a {
      ${createUnderline({})}
    }

    span {
      opacity: .7;
    }

    .codes {
      dl {
        align-items: flex-start;
        display: flex;
        flex-direction: column;
        gap: 4px;
        justify-content: flex-start;
        list-style-type: disc;
      }

      dt {
        ${font12_Nav(true, 400)}
				opacity: .55;
        margin-bottom: 8px;

				${mq.greaterThan('tablet')} {
					${font12_Nav(false, 400)}
				}
      }

      dd {
				${font12_Nav(true, 400)}
        display: list-item;
        list-style-position: inside;
        /* list-style-type: disc; */

				${mq.greaterThan('tablet')} {
					${font12_Nav(false, 400)}
				}
      }
    }
  }

  .links {
    grid-column: 1 / span 6;
    margin: ${vw(70, 'mobile')}  0;

    /* ${mq.greaterThan('nexus7')} {
      margin: ${vw(70, 'tablet')}  0;
    } */

    ${mq.greaterThan('tablet')} {
      ${innerGrid(defaultGrid.tablet)}
      grid-column: 9 / span 4;
      grid-row: 2;
      margin: 0;
    }

    ${mq.greaterThan('desktop')} {
      ${innerGrid(defaultGrid.desktop)}
    }

    ul {
      display:none;
      margin: 0 auto ${vw(150, 'mobile')};
      width: 100%;

      ${mq.greaterThan('tablet')} {
        display: block;
        margin: 0 auto ${vw(150, 'desktop')};
        grid-column: 1 / span 6;
        grid-row: 1;
      }

      ${mq.greaterThan('desktop')} {
        margin: 0 auto 150px;
      }

      li {
        margin-bottom: 10px;

        &:last-child {
          margin-bottom: 0;
        }
        &:first-child {
          display: none;
        }
      }

      a {
        ${createUnderline({})}
        ${font12_Nav(true, 400)}
        text-transform: uppercase;

        ${mq.greaterThan('tablet')} {
          ${font12_Nav(false, 400)}
        }
      }
    }

    dl {
      align-content: flex-start;
      display: flex;
      flex-wrap: wrap;
      margin: 0 auto;
      gap: 20px;

      ${mq.greaterThan('tablet')} {
        margin: 0 auto ${vw(150, 'desktop')};
        grid-column: 5 / span 6;
        grid-row: 1;
      }

      ${mq.greaterThan('desktop')} {
        margin: 0 auto 150px;
      }

      dt {
        ${font12_Nav(true, 400)}
        width: 100%;

        ${mq.greaterThan('tablet')} {
          ${font12_Nav(false, 400)}
        }
      }

      a {
        align-items: center;
        display: flex;
        justify-content: center;

      }

      dd {
        img {
          height: ${vw(20, 'mobile')};
/*
          ${mq.greaterThan('nexus7')} {
            height: ${vw(20, 'tablet')};
          } */

          ${mq.greaterThan('tablet')} {
            height: ${vw(20, 'desktop')};
          }
        }

        &:first-of-type {
          a {
            padding-left: 0;
          }

          img {
            width: ${vw(20, 'mobile')};
            object-fit: contain;
            /* ${mq.greaterThan('nexus7')} {
              width: ${vw(20, 'tablet')};
            } */

            ${mq.greaterThan('tablet')} {
              width: ${vw(20, 'desktop')};
            }
          }
        }

        &:last-child {
          img {
            width: ${vw(25, 'mobile')};

            /* ${mq.greaterThan('nexus7')} {
              width: ${vw(25, 'tablet')};
            } */

            ${mq.greaterThan('tablet')} {
              width: ${vw(25, 'desktop')};
            }
          }
        }
      }

    }
  }

  > p {
    ${font10(true)}
    align-items: flex-end;
    display: flex;
    grid-column: 1 / span 6;
    justify-content: flex-start;
    margin-top: 0;
    gap: 40px;

    /* ${mq.greaterThan('nexus7')} {
      margin-top: 0;
    } */

    ${mq.greaterThan('tablet')} {
      ${font10(false)}
      justify-content: space-between;
      grid-column: 1 / span 12;
      margin-top: 1.5vw;
    }

    a {
      ${font10(true)}
      ${createUnderline({})}
      text-transform: uppercase;

      ${mq.greaterThan('tablet')} {
        ${font10(false)}
      }
    }
  }

	.links--legal {
		display: flex;
		flex-direction: row;
		justify-content: flex-start;
		gap: 10px;
		width: auto;
	}
`
